"use client";

import { Camera, Mic, Eye, Clock, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { INTERVIEW, questionCountForDifficulty } from "@/lib/constants";
import { useDict, fill } from "@/lib/i18n/context";

const ruleIcons = [Camera, Mic, Eye, Clock];

export function ConsentScreen({
  candidateName,
  jobTitle,
  companyName,
  questionCount,
  maxDurationSec,
  onAccept,
}: {
  candidateName: string;
  jobTitle?: string | null;
  companyName?: string | null;
  questionCount?: number;
  maxDurationSec?: number;
  onAccept: () => void;
}) {
  const t = useDict();
  const minutes = Math.round(
    (maxDurationSec ?? INTERVIEW.MAX_DURATION_SEC) / 60,
  );
  const questions = questionCount ?? questionCountForDifficulty("medium");
  const rolePart = jobTitle ? fill(t.consent.forRole, { title: jobTitle }) : "";
  const invited = companyName
    ? fill(t.consent.invitedByCompany, { company: companyName, job: rolePart })
    : fill(t.consent.invitedGeneric, { job: rolePart });

  return (
    <div className="mx-auto max-w-xl animate-fade-in">
      <Card>
        <CardContent className="space-y-6 pt-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold">
              {fill(t.consent.welcomeHi, { name: candidateName.split(" ")[0] })}
            </h1>
            <p className="mt-1 text-muted">{invited}</p>
          </div>

          <div className="space-y-3">
            {t.consent.rules.map((r, i) => {
              const Icon = ruleIcons[i];
              const desc =
                i === 3
                  ? fill(r.desc, {
                      minutes: String(minutes),
                      questions: String(questions),
                    })
                  : r.desc;
              return (
                <div
                  key={r.title}
                  className="flex gap-3 rounded-xl border border-border p-3"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-medium">{r.title}</p>
                    <p className="text-sm text-muted">{desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            <ShieldAlert className="h-5 w-5 shrink-0" />
            <p>{t.consent.note}</p>
          </div>

          <Button className="w-full" size="lg" onClick={onAccept}>
            {t.consent.startSetup}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
